import { useLocale } from '../../preferences/i18n/LocaleProvider';
import { summarizeArchiveSpatialStatus } from '../services/archive-spatial-status';

interface ImportArchiveConfirmDialogProps {
  importing?: boolean;
  currentTitle: string;
  incomingTitle: string;
  warnings: string[];
  onConfirm(): Promise<void> | void;
  onCancel(): void;
}

export function ImportArchiveConfirmDialog({
  importing = false,
  currentTitle,
  incomingTitle,
  warnings,
  onConfirm,
  onCancel,
}: ImportArchiveConfirmDialogProps) {
  const { t } = useLocale();
  const spatialStatus = summarizeArchiveSpatialStatus(warnings);

  return (
    <div role="dialog" aria-modal="true" aria-label={t('archiveImportConfirmTitle')} className="floating-panel">
      <div className="hud-stack" style={{ minWidth: 320, maxWidth: 380 }}>
        <h2 style={{ margin: 0, fontSize: '1rem' }}>{t('archiveImportConfirmTitle')}</h2>
        <p style={{ margin: 0 }}>{formatMessage(t('archiveImportReplacePrompt'), { current: currentTitle, next: incomingTitle })}</p>
        <div className="hud-stack" aria-label={t('archiveSpatialStatusLabel')}>
          <p className="mode-switcher__eyebrow" style={{ margin: 0 }}>{t('archiveSpatialStatusLabel')}</p>
          <p style={{ margin: 0 }}>
            {spatialStatus.hasSpatialFallback ? t('archiveSpatialFallbackStatus') : t('archiveSpatialRoundTripStatus')}
          </p>
          {spatialStatus.spatialWarnings.length > 0 ? (
            <div className="hud-stack" style={{ gap: 6 }}>
              <strong>{t('archiveSpatialWarningsListTitle')}</strong>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {spatialStatus.spatialWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
        {spatialStatus.nonSpatialWarnings.length > 0 && (
          <p style={{ margin: 0 }}>
            {formatMessage(t('archiveImportWarningsPreview'), { warnings: spatialStatus.nonSpatialWarnings.join('; ') })}
          </p>
        )}
        <div className="hud-toolbar" role="group" aria-label={t('archiveImportConfirmTitle')}>
          <button className="hud-subtle-button" disabled={importing} onClick={() => void onConfirm()} type="button">
            {importing ? t('archiveImporting') : t('archiveImportReplace')}
          </button>
          <button className="hud-subtle-button" disabled={importing} onClick={onCancel} type="button">
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatMessage(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce((message, [key, value]) => message.replace(`{${key}}`, value), template);
}